import { useState, useEffect, useMemo, SyntheticEvent } from "react";
import { Box, Grid, Card, CardContent, Typography, Button, Divider, FormControl, TextField, InputLabel, MenuItem, Select, useMediaQuery, Checkbox, FormControlLabel, SelectChangeEvent, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { Type_Expense } from '../../models/type_expense';
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn } from "../../components/table/interface";
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import PermissionButton from "../../components/common/PermissionButton";
import { iconActionColumnSizing, simpleTableIconButtonSx } from "../../components/table/iconActionColumnSizing";

// Interfaces
interface Office { id: number; name: string; }
interface Collect { id: number; name: string; }
interface ExpenseReportRow {
  id: string;
  recordId?: number;
  source: string;
  date: string;
  createdAt?: string;
  expense: string;
  description: string;
  value: number;
  office: string;
  collect: string;
  isTotal?: boolean;
}
interface FilterState {
  startDate: string; endDate: string; officeId: string; selectedCollects: number[];
}
interface FilterStateExpense {
  startDate: string; endDate: string; officeId: string; selectedExpense: number[];
}

interface ReporteGastosProps {
  showAlert: (message: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const HIDDEN_COLLECT_EXPENSE_NAMES = new Set([
  "CIERRE EFECTIVO RUTA",
  "BASE EFECTIVO RUTA",
]);

// Utils
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value);

const normalizeExpenseName = (value: string): string => String(value || "").trim().toUpperCase();

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", "America/Bogota")
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", "America/Bogota")
    : moment.tz(dateString, "America/Bogota");
  return parsed.isValid()
    ? parsed.format("DD/MM/YYYY HH:mm:ss")
    : dateString;
};

const isCurrentOperationalDate = (dateString: string, operationalDate: string): boolean => {
  if (!dateString) return false;

  const parsed = moment.tz(
    dateString,
    ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
    'America/Bogota'
  );

  return parsed.isValid() && parsed.format('YYYY-MM-DD') === operationalDate;
};

// Componente
const ReporteGastos: React.FC<ReporteGastosProps> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Reporte Gastos";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();
  const [datasE, setDatasE] = useState<any[]>([]);
  const [type_expense_id, setType_expense_id] = useState('');
  const [typeexpense, setTypeExpense] = useState<Type_Expense[]>([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate, endDate: currentDate, officeId: "", selectedCollects: [],
  });

  const [filterExpense, setFilterExpense] = useState<FilterStateExpense>({
    startDate: currentDate, endDate: currentDate, officeId: "", selectedExpense: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false); // maestro para cobros
  const [selectAllExpenses, setSelectAllExpenses] = useState(false); // maestro para gastos
  const [rows, setRows] = useState<ExpenseReportRow[]>([]);
  const { drawerOpen } = useDrawer();
  const { canDelete } = useResourcePermissions('/informes/reportegastos');

  // Oficinas
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data?.data) setOffices(response.data.data);
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert("Error al cargar las oficinas. Intente de nuevo más tarde.", "error");
      }
    };
    fetchOffices();
    // Solo debe correr al montar. showAlert cambia de referencia con renders del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cobros por oficina + tipos de gasto
  useEffect(() => {
    const fetchCollectsAndTypes = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/collectsoffice/${filter.officeId}`);
        if (response.data) {
          const nextCollects = Array.isArray(response.data) ? response.data : [];
          setCollectsOffice(nextCollects);
          setFilter((prev) => ({
            ...prev,
            selectedCollects: prev.selectedCollects.filter((selectedId) =>
              nextCollects.some((collect: Collect) => Number(collect.id) === Number(selectedId))
            ),
          }));
          setSelectAll(false);
        }
      } catch (error) {
        console.error("Error fetching collects:", error);
        showAlert("Error al cargar los cobros para la oficina seleccionada.", "error");
      } finally {
        setLoading(false);
      }
      try {
        const { data: expenseData } = await axios.get('typeexpense');
        const expenseTypes = expenseData?.data ?? expenseData ?? [];
        setTypeExpense(Array.isArray(expenseTypes) ? expenseTypes : []);
      } catch (error) {
        showAlert('Error al cargar los tipos de gasto.', 'error');
      }
    };
    fetchCollectsAndTypes();
    // Debe reaccionar solo al cambio real de oficina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.officeId]);

  // Gastos por tipo seleccionado
  useEffect(() => {
    let cancelled = false;
    const fetchExpensesByType = async () => {
      if (!type_expense_id) {
        if (!cancelled) {
          setDatasE([]);
          setFilterExpense((prev) => ({ ...prev, selectedExpense: [] }));
          setSelectAllExpenses(false);
        }
        return;
      }

      try {
        const response = await axios.get(`/expensesbytype/${type_expense_id}`);
        if (!cancelled) {
          const expenses = response.data?.data ?? response.data ?? [];
          const filteredExpenses = Array.isArray(expenses)
            ? expenses.filter((expense: { name?: string }) => {
                const normalizedName = normalizeExpenseName(expense?.name || "");
                return !HIDDEN_COLLECT_EXPENSE_NAMES.has(normalizedName);
              })
            : [];
          setDatasE(filteredExpenses);
          setFilterExpense((prev) => ({
            ...prev,
            selectedExpense: prev.selectedExpense.filter((selectedId) =>
              filteredExpenses.some((expense: { id: number }) => Number(expense.id) === Number(selectedId))
            ),
          }));
          setSelectAllExpenses(false);
        }
      } catch (error) {
        console.error('Error fetching expenses by type:', error);
        showAlert('Error al cargar los gastos por tipo.', 'error');
      }
    };
    fetchExpensesByType();
    return () => { cancelled = true; };
    // Debe reaccionar solo al cambio real de tipo de gasto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type_expense_id]);

  // Handlers básicos
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    setFilter((prev) => ({ ...prev, officeId: val })); // string
  };

  const handleTypeExpenseChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value as string;
    setType_expense_id(value);
    setDatasE([]);
    setSelectAllExpenses(false);
    // No resetear selectedExpense aquí para mantener la selección
  };

  // ✅ Cobros: marcar/desmarcar todos
  const handleSelectAllCollects = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSelectAll(checked);
    setFilter((prev) => ({
      ...prev,
      selectedCollects: checked ? collectsOffice.map((c) => c.id) : [],
    }));
  };

  const handleCollectChange = (collectId: number, checked: boolean) => {
    setFilter((prev) => {
      const set = new Set(prev.selectedCollects);
      if (checked) set.add(collectId);
      else set.delete(collectId);
      const next = Array.from(set);
      setSelectAll(next.length === collectsOffice.length && collectsOffice.length > 0);
      return { ...prev, selectedCollects: next };
    });
  };

  // ✅ Gastos: marcar/desmarcar todos (usa datasE)
  const handleSelectAllExpenses = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSelectAllExpenses(checked);
    setFilterExpense((prev) => ({
      ...prev,
      selectedExpense: checked ? datasE.map((e: { id: number }) => e.id) : [],
    }));
  };

  const handleExpenseChange = (expenseId: number, checked: boolean) => {
    setFilterExpense((prev) => {
      const set = new Set(prev.selectedExpense);
      if (checked) set.add(expenseId);
      else set.delete(expenseId);
      const next = Array.from(set);
      setSelectAllExpenses(next.length === datasE.length && datasE.length > 0);
      return { ...prev, selectedExpense: next };
    });
  };

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert("Debe seleccionar una oficina", "warning");
      return false;
    }
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro", "warning");
      return false;
    }
    if (filterExpense.selectedExpense.length === 0) {
      showAlert("Debe seleccionar al menos un gasto", "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert("La fecha de inicio no puede ser mayor a la fecha final", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    showLoading();

    try {
      const payload = {
        start_date: filter.startDate,
        end_date: filter.endDate,
        office_id: filter.officeId,
        collect_ids: filter.selectedCollects,
        expense_ids: filterExpense.selectedExpense,
      };

      const response = await axios.post("/inform/expenses", payload);
      const reportRows: ExpenseReportRow[] = Array.isArray(response.data?.data)
        ? response.data.data
          .filter((row: ExpenseReportRow) => {
            if (row.source !== "COBRO") return true;
            return !HIDDEN_COLLECT_EXPENSE_NAMES.has(normalizeExpenseName(row.expense));
          })
          .map((row: ExpenseReportRow, index: number) => ({
            ...row,
            id: row.id || `row-${index}`,
          }))
        : [];

      if (reportRows.length === 0) {
        showAlert("No se encontraron gastos en el período seleccionado", "info");
        setRows([]);
        return;
      }

      setRows(reportRows);
      showAlert(`Se encontraron ${reportRows.length} gastos`, "success");
    } catch (error) {
      console.error("Error fetching expenses:", error);
      showAlert("Error al obtener los gastos. Inténtelo de nuevo más tarde.", "error");
    } finally {
      hideLoading();
    }
  };

  const handleDeleteExpense = async (row: ExpenseReportRow) => {
    if (!row.recordId || !['OFICINA', 'COBRO'].includes(row.source) || row.isTotal) {
      return;
    }

    if (!isCurrentOperationalDate(row.date, currentDate)) {
      showAlert('Solo se pueden eliminar registros de la fecha actual.', 'warning');
      return;
    }

    showLoading();

    try {
      await axios.delete(`/inform/expenses/${row.source}/${row.recordId}`);
      setRows((prevRows) => prevRows.filter((item) => item.id !== row.id));
      showAlert('Registro eliminado correctamente.', 'success');
    } catch (error: any) {
      const message =
        error?.response?.data?.message || 'Error al eliminar el registro.';
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const columns: SimpleColumn[] = [
    {
      field: "date",
      headerName: "Fecha",
      minWidth: 80,
      maxWidth: 80,
      align: "center",
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatDate(row.createdAt || row.date)}</Typography>
      ),
    },
    {
      field: "expense",
      headerName: "Gasto",
      minWidth: 80,
      maxWidth: 120,
      wrapText: true,
    },
    {
      field: "collect",
      headerName: "Cobro",
      minWidth: 80,
      maxWidth: 120,
      wrapText: true,
    },
    {
      field: "value",
      headerName: "Valor",
      align: "right",
      minWidth: 50,
      maxWidth: 50,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={row.isTotal ? { fontWeight: 700 } : undefined}>
          {formatCurrency(Number(row.value ?? 0))}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Descripción",
      minWidth: 220,
      maxWidth: 220,
      wrapText: true,
    },    
    {
      field: "actions",     
      headerName: "Opciones",
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }) => {
        const expenseRow = row as ExpenseReportRow;
        const canDeleteRow =
          !expenseRow.isTotal &&
          ['OFICINA', 'COBRO'].includes(expenseRow.source) &&
          Boolean(expenseRow.recordId) &&
          isCurrentOperationalDate(expenseRow.date, currentDate);

        if (!canDeleteRow) {
          return null;
        }

        return (
          <PermissionButton
            allowed={canDelete}
            deniedTooltip="No tienes permiso para eliminar registros de gastos."
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDeleteExpense(expenseRow)}
            sx={simpleTableIconButtonSx}
          >
            <DeleteIcon fontSize="small" />
          </PermissionButton>
        );
      },
    },
  ];

  const rowsWithTotal = useMemo<ExpenseReportRow[]>(() => {
    const totalValue = rows.reduce((sum, row) => sum + Number(row.value ?? 0), 0);
    return [
      ...rows,
      {
        id: "total-row",
        source: "TOTAL",
        date: "",
        createdAt: "",
        expense: "",
        description: "",
        value: totalValue,
        office: "",
        collect: "",
        isTotal: true,
      },
    ];
  }, [rows]);

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Reporte Tipo Gasto</Title>
        <Box>
          <Button type="submit" form="form-gastos" variant="contained" startIcon={!isMobile && <SearchIcon />}>
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>

      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <form id="form-gastos" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <Grid container spacing={1}>
                  {/* Fechas */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth name="startDate" size="small" type="date" label="Fecha Inicio"
                      value={filter.startDate} onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth name="endDate" size="small" type="date" label="Fecha Fin"
                      value={filter.endDate} onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }} inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                    />
                  </Grid>

                  {/* Oficina */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Oficina</InputLabel>
                      <Select
                        value={filter.officeId}
                        size="small"
                        label="Oficina"
                        onChange={handleOfficeChange}
                      >
                        {offices.map((office) => (
                          <MenuItem key={office.id} value={String(office.id)}>
                            {office.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tipo de gasto (único) */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Seleccionar Tipo de Gasto</InputLabel>
                      <Select
                        size="small"
                        value={type_expense_id}
                        onChange={handleTypeExpenseChange}
                        label="Seleccionar Tipo de Gasto"
                        required
                      >
                        <MenuItem value="">Seleccionar gasto</MenuItem>
                        {typeexpense.map((te) => (
                          <MenuItem key={te.id} value={te.id.toString()}>{te.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Cobros (5 columnas) */}
                  {collectsOffice.length > 0 && (
                    <Grid item xs={12} spacing={1}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          Seleccionar Cobros:
                        </Typography>
                        {loading && <CircularProgress size={18} />}
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={filter.selectedCollects.length === collectsOffice.length && collectsOffice.length > 0}
                              onChange={handleSelectAllCollects}
                              indeterminate={
                                filter.selectedCollects.length > 0 &&
                                filter.selectedCollects.length < collectsOffice.length
                              }
                            />
                          }
                          label="Marcar/Desmarcar todos"
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(200px, 1fr))",
                            md: "repeat(3, minmax(200px, 1fr))",
                            lg: "repeat(4, minmax(200px, 1fr))",
                            xl: "repeat(5, minmax(200px, 1fr))",
                          },
                          columnGap: 0.05,   // antes: 1
                          rowGap: 0.05,      // antes: 1 -> menos espacio vertical entre filas
                        }}
                      >
                        {collectsOffice.map((collect) => (
                          <FormControlLabel
                            key={collect.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={filter.selectedCollects.includes(collect.id)}
                                onChange={(e) => handleCollectChange(collect.id, e.target.checked)}
                                value={collect.id.toString()}
                              />
                            }
                            label={collect.name}
                            sx={{
                              m: 0,        // sin márgenes externos
                              py: 0.05,    // acolchado vertical mínimo
                              "& .MuiFormControlLabel-label": {
                                lineHeight: 1.1,   // etiqueta más compacta
                                fontSize: "0.7rem" // opcional: más pequeña
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Gastos (5 columnas) */}
                  {datasE.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          Seleccionar Gastos:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={filterExpense.selectedExpense.length === datasE.length && datasE.length > 0}
                              onChange={handleSelectAllExpenses}
                              indeterminate={
                                filterExpense.selectedExpense.length > 0 &&
                                filterExpense.selectedExpense.length < datasE.length
                              }
                            />
                          }
                          label="Marcar/Desmarcar todos"
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(200px, 1fr))",
                            md: "repeat(3, minmax(200px, 1fr))",
                            lg: "repeat(4, minmax(200px, 1fr))",
                            xl: "repeat(5, minmax(200px, 1fr))",
                          },
                          columnGap: 0.75,   // antes: 1
                          rowGap: 0.25,      // antes: 1 -> menos espacio vertical entre filas
                        }}
                      >
                        {datasE.map((exp: { id: number; name: string }) => (
                          <FormControlLabel
                            key={exp.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={filterExpense.selectedExpense.includes(exp.id)}
                                onChange={(e) => handleExpenseChange(exp.id, e.target.checked)}
                                value={exp.id.toString()}
                              />
                            }
                            label={exp.name}
                            sx={{
                              m: 0,        // sin márgenes externos
                              py: 0,    // acolchado vertical mínimo
                              "& .MuiFormControlLabel-label": {
                                lineHeight: 1.1,   // etiqueta más compacta
                                fontSize: "0.9rem" // opcional: más pequeña
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rowsWithTotal}
              columns={columns}
              disableInternalScroll
              showBorders
              showCount
              exportCsv
              exportFileName="reporte-gastos.csv"
              wrapCellContent
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
    </HeaderResponsive>
  );
};

export default ReporteGastos;
